import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, X } from 'lucide-react';
import { Button } from '../ui/button'; // Assuming shadcn/ui path
import type { Database } from '../../types/supabase';

// Define the type for a notification row, assuming it's generated in supabase.ts
type Notification = Database['public']['Tables']['notifications']['Row'];

interface NotificationListProps {
  onClose: () => void;
}

const NotificationList: React.FC<NotificationListProps> = ({ onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20); // Fetch latest 20 notifications

      if (fetchError) throw fetchError;
      setNotifications(data || []);
    } catch (err: any) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications.');
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();

    // Optional: Set up realtime listener specifically for the list if needed,
    // although the Bell component's listener might be sufficient if it triggers
    // a state update that causes NotificationList to re-render and fetch.
    // For simplicity, we rely on manual fetch on open and potentially after marking read.

  }, [fetchNotifications]);

  const handleMarkAsRead = async (notificationId: string) => {
    if (!user) return;
    try {
      const { error: updateError } = await supabase
        .from('notifications')
        .update({ read: true })
        .match({ id: notificationId, user_id: user.id });

      if (updateError) throw updateError;

      // Update local state immediately for better UX
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      // Optionally, trigger a refetch of the count in the parent (Bell) if needed
      // This might happen automatically if state changes propagate up or via the realtime listener.
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      // Optionally show an error message to the user
    }
  };

  const handleMarkAllAsRead = async () => {
      if (!user || notifications.filter(n => !n.read).length === 0) return;
      try {
          const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
          const { error: updateError } = await supabase
              .from('notifications')
              .update({ read: true })
              .in('id', unreadIds)
              .eq('user_id', user.id);

          if (updateError) throw updateError;

          // Update local state
          setNotifications(prev => prev.map(n => ({ ...n, read: true })));
          // Trigger count update in parent if needed
      } catch (err) {
          console.error('Failed to mark all notifications as read:', err);
      }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read first
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }
    // Navigate using react-router-dom if a link exists
    if (notification.link) {
      console.log('Navigating to:', notification.link); 
      navigate(notification.link); // Use navigate for SPA navigation
    }
    onClose(); // Close the list after clicking
  };

  return (
    <div className="rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
        <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center items-center h-20">
            <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
          </div>
        ) : error ? (
          <p className="p-4 text-center text-sm text-red-600">{error}</p>
        ) : notifications.length === 0 ? (
          <p className="p-4 text-center text-sm text-gray-500">No new notifications.</p>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleNotificationClick(notification);
                }
              }}
              className={`p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${notification.read ? 'opacity-70' : 'font-medium'}`}
            >
              <div className="flex items-start space-x-2">
                {!notification.read && (
                  <span className="mt-1 h-2 w-2 rounded-full bg-primary flex-shrink-0"></span>
                )}
                <div className="flex-grow">
                  <p className="text-sm text-gray-800">
                    {notification.title && <span className="font-semibold">{notification.title}: </span>}
                    {notification.message}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {!isLoading && !error && notifications.some(n => !n.read) && (
          <div className="p-2 border-t border-gray-200 text-center">
              <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="text-xs text-primary">
                  Mark all as read
              </Button>
          </div>
      )}
    </div>
  );
};

export default NotificationList; 